import React, { useEffect, useState } from 'react'

import { Keypair, LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { useLocalWalletBundlr } from 'hooks/useLocalWalletBundlr'
import { useMetaplex } from 'hooks/useMetaplex'
import { toMetaplexFile } from '@metaplex-foundation/js'

// images and icons
import DAOLogoIcon from 'assets/icons/dao-logo-small.svg?component'
import DAOLogoMobile from 'assets/icons/dao-logo-small-mobile.svg?component'
import SolIcon from 'assets/icons/solana-logo-dark-small.svg?component'
import SolMobileIcon from 'assets/icons/solana-logo-13x11.svg?component'

// styles
import styles from './index.module.scss'

import axios from 'axios'
import { useViewport, ensureMaxDecimals } from 'utils'
import StatsSearchBox from 'components/Input/StatsSearchBox'
import TableSection from './TableSection'
import DaoSubmitModal from 'components/Modal/DaoSubmitModal'
import Alert from 'components/Alert'
import { trySendTransaction } from 'utils'
import { isExpiredDate } from 'utils/helpers'
import { DaoProposal, createNewProposalTransaction, DAO_TREASURY_ADDRESS, getCurrentTurkeyNftCount } from 'utils/dao'
import ProgressingModalWithContent from 'components/Modal/ProgressingModalWithContent'
import { useRouter } from 'next/router'
import { useSolToUsdRate } from 'hooks/useSolToUsdRate'

const DaoLandingPage = props => {
  const { turkyNfts, nftIsLoaded } = props
  const viewport = useViewport()
  const router = useRouter()
  const { connected, publicKey, signTransaction } = useWallet()
  const { storage, ensureLocalWalletBalance } = useLocalWalletBundlr()
  const { metaplex } = useMetaplex()
  const { setVisible } = useWalletModal()
  const { solToUsdRate } = useSolToUsdRate()

  const [searchWord, setSearchWord] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [liveCount, setLiveCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [closedCount, setClosedCount] = useState(0)
  const [rowCount, setRowCount] = useState(10)
  const [showDaoSubmitModal, setShowDaoSubmitModal] = useState(false)
  const [clickedSubmitButton, setClickedSubmitButton] = useState(false)

  const [solAmount, setSolAmount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isNftLoading, setIsNftLoading] = useState(false)
  const [alertData, setAlertData] = useState<any[]>([])
  const [noTurkeyAlert, setNoTurkeyAlert] = useState<any[]>([])

  const [showAlert, setShowAlert] = useState(false)
  const [showNoTurkeyAlert, setShowNoTurkeyAlert] = useState(false)
  const [proposals, setProposals] = useState([])
  const [proposalKeypair, setProposalKeypair] = useState<Keypair>(Keypair.generate())

  useEffect(() => {
    metaplex.connection.getBalance(DAO_TREASURY_ADDRESS).then(lamports => setSolAmount(lamports / LAMPORTS_PER_SOL))

    setNoTurkeyAlert([
      {
        id: 0,
        title: 'CircusDAO Membership Not Detected',
        description: `The wallet you've connected with does not hold a Turkey Circus NFT`,
        type: 'error',
      },
    ])

    getProposals(currentPage, rowCount)

    getStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (nftIsLoaded) setIsNftLoading(false)
  }, [nftIsLoaded])

  const selectRowCount = count => {
    setRowCount(count)
  }

  const handleNextPage = () => {
    const nextPage = currentPage + 1
    getProposals(nextPage, rowCount)
  }

  const handlePreviousPage = () => {
    if (currentPage === 0) return
    const prevPage = currentPage - 1
    getProposals(prevPage, rowCount)
  }

  const onClickSubmitBtn = async () => {
    if (!connected) {
      setClickedSubmitButton(true)
      setVisible(true)
      return
    }

    if (nftIsLoaded === false) {
      setIsNftLoading(true)
      return
    }

    if (turkyNfts.length === 0) {
      setShowNoTurkeyAlert(true)
      return
    }
    setShowDaoSubmitModal(true)
  }

  const handleSubmitProposal = async proposal => {
    setIsLoading(true)
    try {
      // TODO: calculate json size
      // for now, just fund with 0.001 sol which approximately 3MB
      await ensureLocalWalletBalance(0.001 * 1e9)

      let voteDurationMs = proposal.vote_duration * 60 * 60 * 1000
      let voteEnd = new Date(proposal.vote_start.getTime() + voteDurationMs)
      let daoProposal: DaoProposal = {
        proposalKey: proposalKeypair.publicKey.toBase58(),
        proposer: publicKey.toBase58(),
        category: proposal.category,
        title: proposal.title,
        overview: proposal.overview,
        metrics: proposal.metrics,
        treasurySpendAmount: `${proposal.treasury} SOL`,
        timeline: proposal.eta_timeline,
        voteStart: proposal.vote_start.toISOString(),
        voteEnd: voteEnd.toISOString(),
      }

      let daoMetaplexFile = toMetaplexFile(JSON.stringify(daoProposal, null, 4), 'proposal.json')
      const url = await storage.upload(daoMetaplexFile)

      let tx = createNewProposalTransaction({
        proposalKey: proposalKeypair.publicKey,
        proposer: publicKey,
        title: daoProposal.title,
        url,
      })
      tx.feePayer = publicKey

      // TODO: add error handling
      let signature: null | string = await trySendTransaction(metaplex.connection, tx, async tx => {
        tx.partialSign(proposalKeypair)
        return signTransaction(tx)
      })
      if (!signature) throw new Error(`could not submit proposal to chain`)

      await axios.post('/api/dao/submit', {
        signature,
        ...proposal,
        url,
        secretKey: Buffer.from(proposalKeypair.secretKey).toString('hex'),
      })

      setProposalKeypair(Keypair.generate())

      setAlertData([
        {
          id: 0,
          title: 'Proposal Submitted',
          description: `${proposal.title} has successfully been submitted for community voting.`,
          type: 'success',
        },
      ])

      getProposals(currentPage, rowCount)

      setShowDaoSubmitModal(false)

      setTimeout(() => {
        setIsLoading(false)
        setTimeout(() => {
          setShowAlert(true)
        }, 1000)
      }, 1000)
    } catch (e: any) {
      console.error(e)
      setTimeout(() => {
        setIsLoading(false)
      }, 1000)
    }
  }

  const getStats = async () => {
    try {
      let response = await axios.get('/api/dao/stats')
      if (response.data.success === true) {
        let stats = response.data.stats
        setTotalCount(stats.total_count)
        setLiveCount(stats.live_count)
        setPendingCount(stats.pending_count)
        setClosedCount(stats.closed_count)
      }
    } catch {}
  }

  const getThreshold = async () => {
    return Math.trunc((await getCurrentTurkeyNftCount()) * 0.51)
  }

  const getProposals = async (curPage, count) => {
    try {
      let response = await axios.post('/api/dao/proposals', { page: curPage, rows: count })
      if (response.data.success === true) {
        let proposals = response.data.proposals
        if (proposals.length > 0) {
          setCurrentPage(curPage)
        } else {
          return
        }
        let new_proposals = []
        proposals.forEach(element => {
          if (isExpiredDate(element.vote_start, element.vote_duration)) {
            let current_threshold = getThreshold()
            let vote_ratio = element.approved_votes / element.total_votes
            new_proposals.push({
              ...element,
              status: 'closed',
              vote_result: vote_ratio >= 0.7 && element.total_votes >= current_threshold ? 'Passed' : 'Rejected',
              // vote_result: 'Passed',
            })
          } else if (isExpiredDate(element.vote_start, 0)) {
            new_proposals.push({ ...element, status: 'live' })
          } else {
            new_proposals.push({ ...element, status: 'pending' })
          }
        })
        setProposals(new_proposals)
      }
    } catch {}
  }

  return (
    <div className={styles.container}>
      {viewport === 'lg' || viewport === 'xl' || viewport === '2xl' ? (
        <div className={styles.topSection}>
          <div className="w-full g-container">
            <div className="w-full flex justify-between items-start">
              <DAOLogoIcon className={styles.daoLogoIcon} />
              <div className="flex gap-[15px] items-center">
                <div className="flex gap-[6px]">
                  <div className={styles.statsValue}>{totalCount}</div>
                  <div className={styles.statsTitle}>All</div>
                </div>
                <div className={styles.divider}></div>
                <div className="flex gap-[6px]">
                  <div className={styles.statsValue}>{liveCount}</div>
                  <div className={styles.statsTitle}>Live</div>
                </div>
                <div className={styles.divider}></div>
                <div className="flex gap-[6px]">
                  <div className={styles.statsValue}>{pendingCount}</div>
                  <div className={styles.statsTitle}>Pending</div>
                </div>
                <div className={styles.divider}></div>
                <div className="flex gap-[6px]">
                  <div className={styles.statsValue}>{closedCount}</div>
                  <div className={styles.statsTitle}>Closed</div>
                </div>
              </div>
            </div>

            <div className={styles.treasuryContainer}>
              <div>
                <div className={styles.treasuryLabel}>Treasury</div>
                <div className="flex gap-[15px] mt-[10px]">
                  <div className="flex gap-[6px] items-center">
                    <SolMobileIcon className={styles.solIcon} />
                    <div className={styles.treasuryTitle}>{ensureMaxDecimals(solAmount, 3)}</div>
                  </div>
                  <div className={styles.treasuryDivider}></div>
                  <div className="flex items-center">
                    <div className={styles.treasuryTitle}>${ensureMaxDecimals(solToUsdRate * solAmount, 2)}</div>
                  </div>
                </div>
              </div>
              <div className={styles.landingTitle}>
                CircusDAO is the governing body of Foster Marketplace. Its treasury funds community proposals that are
                submitted and voted on by verified Turkey Circus holders.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className={styles.topSection}>
            <div className="flex justify-center">
              <DAOLogoMobile className={styles.daoLogoIcon} />
            </div>

            <div className="g-container mt-[30px]">
              <div>
                <div className={styles.treasuryLabel}>Treasury</div>
                <div className="flex gap-[15px] mt-[10px]">
                  <div className="flex gap-[6px] items-center">
                    <SolIcon className={styles.solIcon} />
                    <div className={styles.treasuryTitle}>{ensureMaxDecimals(solAmount, 3)}</div>
                  </div>
                  <div className={styles.treasuryDivider}></div>
                  <div className="flex items-center">
                    <div className={styles.treasuryTitle}>${ensureMaxDecimals(solToUsdRate * solAmount, 2)}</div>
                  </div>
                </div>
              </div>
              <div className={styles.landingTitle}>
                CircusDAO is the governing body of Foster Marketplace. Its treasury funds community proposals that are
                submitted and voted on by verified Turkey Circus holders.
              </div>
            </div>
          </div>
          <div className="w-full flex flex-wrap gap-[5px] mt-[30px] g-container">
            <div className="w-full flex gap-[5px]">
              <div className={styles.statsBG}>
                <div>
                  <div className={styles.statsValue}>{totalCount}</div>
                  <div className={styles.statsTitle}>ALL</div>
                </div>
              </div>
              <div className={styles.statsBG}>
                <div>
                  <div className={styles.statsValue}>{liveCount}</div>
                  <div className={styles.statsTitle}>LIVE</div>
                </div>
              </div>
            </div>
            <div className="w-full flex gap-[5px]">
              <div className={styles.statsBG}>
                <div>
                  <div className={styles.statsValue}>{pendingCount}</div>
                  <div className={styles.statsTitle}>PENDING</div>
                </div>
              </div>
              <div className={styles.statsBG}>
                <div>
                  <div className={styles.statsValue}>{closedCount}</div>
                  <div className={styles.statsTitle}>CLOSED</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="g-container w-full hidden lg:mt-[30px] lg:flex justify-between">
        <StatsSearchBox
          value={searchWord}
          placeholder="Search by proposal name"
          onChange={value => setSearchWord(value)}
        />
        <div className={styles.submitProposal} onClick={() => onClickSubmitBtn()}>
          {/* <NotificationIcon /> */}
          <div className={styles.submitText}>Submit Proposal</div>
        </div>
      </div>

      <div className="g-container w-full mt-[30px] lg:hidden justify-between">
        <div className="w-full flex justify-end mb-[15px]">
          <div className={styles.submitProposal} onClick={() => onClickSubmitBtn()}>
            <div className={styles.submitText}>Submit Proposal</div>
          </div>
        </div>
        <StatsSearchBox
          value={searchWord}
          placeholder="Search by proposal name"
          onChange={value => setSearchWord(value)}
        />
      </div>

      <div className="mt-[15px] lg:mt-[16px] g-container">
        <TableSection
          data={proposals}
          onDetail={item => {
            router.push(`/dao/proposal${item.id}`)
          }}
        />
      </div>
      <div className="w-full h-[100px]" />
      <DaoSubmitModal
        data={{}}
        open={showDaoSubmitModal}
        onSubmit={proposal => {
          setTimeout(() => {
            handleSubmitProposal(proposal)
          }, 1000)
        }}
        onClose={() => {
          setIsLoading(false)
          setShowDaoSubmitModal(false)
        }}
        isLoading={isLoading}
      />

      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={alertData}
      />

      <Alert
        open={showNoTurkeyAlert}
        close={() => {
          setShowNoTurkeyAlert(false)
        }}
        data={noTurkeyAlert}
      />

      {/* <ProgressingModal open={isLoading} /> */}

      <ProgressingModalWithContent
        open={isNftLoading}
        close={() => {
          setIsNftLoading(false)
        }}
        title="Wallet detection in progress..."
        tip="Fun Fact"
        content="If you have many NFTs in your wallet, this may take a little longer."
      />
    </div>
  )
}

export default DaoLandingPage
